import { useState } from "react";
import { Archive, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  archiveCompany,
  bulkArchiveCompanies,
} from "@/api/companies";
import type { Company } from "@/api/types";

interface SingleArchiveProps {
  mode: "single";
  projectSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: Pick<Company, "id" | "name" | "sector"> | null;
  /** Optional contact count, shown in the summary card. */
  contactCount?: number;
  onArchived: (company: Company) => void;
}

interface BulkArchiveProps {
  mode: "bulk";
  projectSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ids: number[];
  onArchived: (count: number) => void;
}

export type ArchiveCompanyDialogProps = SingleArchiveProps | BulkArchiveProps;

export function ArchiveCompanyDialog(props: ArchiveCompanyDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const isBulk = props.mode === "bulk";
  const count = isBulk ? props.ids.length : 1;

  async function handleArchive() {
    setSubmitting(true);
    try {
      if (props.mode === "single") {
        if (!props.company) return;
        const updated = await archiveCompany(
          props.projectSlug,
          props.company.id,
        );
        toast.success("Company archived");
        props.onArchived(updated);
      } else {
        const res = await bulkArchiveCompanies(props.projectSlug, props.ids);
        toast.success(
          `${res.archived} compan${res.archived > 1 ? "ies" : "y"} archived`,
        );
        props.onArchived(res.archived);
      }
      props.onOpenChange(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={(o) => !submitting && props.onOpenChange(o)}
    >
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600">
            <Archive className="h-5 w-5" />
          </div>
          <DialogTitle className="text-center text-lg">
            {isBulk
              ? `Archive ${count} compan${count > 1 ? "ies" : "y"}?`
              : "Archive this company?"}
          </DialogTitle>
          <DialogDescription className="text-center text-sm leading-relaxed">
            {isBulk
              ? "These companies will be removed from active views, but their data will be kept. You can restore them at any time."
              : "The company will be removed from active views, but its data will be kept. You can restore it at any time."}
          </DialogDescription>
        </DialogHeader>

        {props.mode === "single" && props.company && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3">
            <div className="text-sm font-medium text-slate-900">
              {props.company.name}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              {props.company.sector && <span>{props.company.sector}</span>}
              {props.company.sector && props.contactCount != null && (
                <span className="text-slate-300">·</span>
              )}
              {props.contactCount != null && (
                <span>
                  {props.contactCount} contact
                  {props.contactCount === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => props.onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleArchive}
            disabled={submitting}
            className="bg-amber-500 text-white hover:bg-amber-600"
          >
            {submitting ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Archive className="mr-2 h-3.5 w-3.5" />
            )}
            Archive
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
  }
  return "Couldn't archive.";
}
